import axios from 'axios';
import { BASE_URL } from '../../../../../../utils';
const API_BASE_URL = 'http://172.16.2.46:4000/api';

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const token = user?.token; // safely access user.token

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);


// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {

    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  getProfile: () => api.get("/auth/me"),
  updateProfile: (data) => api.put("/auth/profile", data),
  changePassword: (data) => api.put("/auth/change-password", data),
};

// Medicines API
export const medicinesAPI = {
  getAll: (params) => api.get("/medicines", { params }),
  getById: (id) => api.get(`/medicines/${id}`),
  create: (data) => api.post("/medicines", data),
  update: (id, data) => api.put(`/medicines/${id}`, data),
  delete: (id) => api.delete(`/medicines/${id}`),
  checkInteractions: (medicineIds) =>
    api.post("/medicines/check-interactions", { medicineIds }),
  getStockHistory: (id, params) =>
    api.get(`/medicines/${id}/stock-history`, { params }),
  getBatches: (id) => api.get(`/medicines/${id}/batches`),
  getBySupplier: (supplierId, params = {}) =>
    api.get(`/medicines/supplier/${supplierId}`, { params }),
};

// Inventory API
export const inventoryAPI = {
  getStockSummary: (params) => api.get("/inventory/stock-summary", { params }),
  getExpiryAlerts: () => api.get("/inventory/expiry-alerts"),
  adjustStock: (data) => api.post("/inventory/adjust-stock", data),
  getRackUtilization: () => api.get("/inventory/rack-utilization"),
  transferRack: (data) => api.put("/inventory/transfer-rack", data),
};

// Sales API
export const salesAPI = {
  getAll: (params) => api.get("/sales", { params }),
  getById: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post("/sales", data),
  updatePayment: (id, data) => api.put(`/sales/${id}/payment`, data),
  update: (id, data) => api.put(`/sales/${id}`, data),
  cancel: (id) => api.put(`/sales/${id}/cancel`),
  checkAvailability: (data) => api.post("/sales/check-availability", data),
  get: (id) => api.get(`/sales/${id}`),
};

export const returnAPI = {
  getAll: (params) => api.get("/returns", { params }),
  getById: (id) => api.get(`/returns/${id}`),
  create: (data) => api.post("/returns", data),
  approve: (id, data) => api.put(`/returns/${id}/approve`, data),
  reject: (id, data) => api.put(`/returns/${id}/reject`, data),
  complete: (id) => api.put(`/returns/${id}/complete`),
  getBySaleInvoice: (saleInvoiceId) =>
    api.get(`/returns/sale/${saleInvoiceId}`),
  checkEligibility: (saleInvoiceId) =>
    api.get(`/returns/check-eligibility/${saleInvoiceId}`),
};

// Purchase API
export const purchaseAPI = {
  getAll: (params) => api.get("/purchases", { params }),
  getById: (id) => api.get(`/purchases/${id}`),
  create: (data) => api.post("/purchases", data),
  update: (id, data) => api.put(`/purchases/${id}`, data),
  delete: (id) => api.delete(`/purchases/${id}`),
  receive: (id, data) => api.put(`/purchases/${id}/receive`, data),
  partialReceive: (id, data) =>
    api.put(`/purchases/${id}/partial-receive`, data),
  updateStatus: (id, data) => api.put(`/purchases/${id}/status`, data),
};


// Suppliers API
export const suppliersAPI = {
  // Basic CRUD
  getAll: (params) => api.get("/suppliers", { params }),
  getById: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post("/suppliers", data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  // Add to suppliersAPI
  getSuppliersByMedicine: (medicineId, params) =>
    api.get(`/suppliers/medicines/${medicineId}`, { params }),

  // Performance & Analytics
  getPerformance: (id, params) =>
    api.get(`/suppliers/${id}/performance`, { params }),
  getAnalytics: (id, params) =>
    api.get(`/suppliers/${id}/analytics`, { params }),

  // Documents
  getDocuments: (id) => api.get(`/suppliers/${id}/documents`),
  uploadDocument: (id, formData) =>
    api.post(`/suppliers/${id}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  downloadDocument: (id, documentId) =>
    api.get(`/suppliers/${id}/documents/${documentId}`),
  deleteDocument: (id, documentId) =>
    api.delete(`/suppliers/${id}/documents/${documentId}`),

  // Communications
  getCommunications: (id) => api.get(`/suppliers/${id}/communications`),
  addCommunication: (id, data) =>
    api.post(`/suppliers/${id}/communications`, data),
  deleteCommunication: (id, communicationId) =>
    api.delete(`/suppliers/${id}/communications/${communicationId}`),

  // Medicines
  getMedicines: (id) => api.get(`/suppliers/${id}/medicines`),
  addMedicine: (id, data) => api.post(`/suppliers/${id}/medicines`, data),
  updateMedicine: (id, medicineId, data) =>
    api.put(`/suppliers/${id}/medicines/${medicineId}`, data),
  removeMedicine: (id, medicineId) =>
    api.delete(`/suppliers/${id}/medicines/${medicineId}`),

  // Payments
  getPayments: (id, params) => api.get(`/suppliers/${id}/payments`, { params }),
  //addPayment: (id, data) => api.post(`/suppliers/${id}/payments`, data),

  getFinancialSummary: (supplierId) =>
    api.get(`/suppliers/${supplierId}/financial-summary`),

  getPaymentAnalytics: (supplierId, params = {}) =>
    api.get(`/suppliers/${supplierId}/payments/analytics`, { params }),

  // Enhanced addPayment to handle new payment types
  addPayment: (supplierId, paymentData) =>
    api.post(`/suppliers/${supplierId}/payments`, paymentData),

  //getFinancialSummary: (id) => api.get(`/suppliers/${id}/financial-summary`),
  getGRNs: (id, params = {}) => api.get(`/suppliers/${id}/grns`, { params }),
  getUnpaidGRNs: (id) => api.get(`/suppliers/${id}/unpaid-grns`),



  getReturns: (id, params = {}) => api.get(`/suppliers/${id}/returns`, { params }),
  createReturn: (id, data) => api.post(`/suppliers/${id}/returns`, data),
  updateReturnStatus: (id, returnId, data) => api.put(`/suppliers/${id}/returns/${returnId}/status`, data),
  getReturnStatistics: (id) => api.get(`/suppliers/${id}/returns/statistics`),

  getStockAdjustments: (medicineId, params = {}) => api.get(`/medicines/${medicineId}/stock-adjustments`, { params }),


  searchBatchesForReturn: (supplierId, searchParams) =>
    api.get(`/suppliers/${supplierId}/returns/available-batches`, { params: searchParams }),

  // Create return from selected batches
  createReturnFromBatches: (supplierId, returnData) =>
    api.post(`/suppliers/${supplierId}/returns/from-batches`, returnData),

  // Process return (approve and adjust stock)
  processReturn: (supplierId, returnId) =>
    api.post(`/suppliers/${supplierId}/returns/${returnId}/process`),
};

// Rack API
export const racksAPI = {
  getAll: (params) => api.get("/racks", { params }),
  getById: (id) => api.get(`/racks/${id}`),
  create: (data) => api.post("/racks", data),
  update: (id, data) => api.put(`/racks/${id}`, data),
  delete: (id) => api.delete(`/racks/${id}`),
  getUtilization: () => api.get("/racks/utilization"),
  getAvailable: () => api.get("/racks/available"),
  transferStock: (data) => api.post("/racks/transfer-stock", data),
};

// Reports API
export const reportsAPI = {
  getSalesReport: (params) => api.get("/reports/sales", { params }),
  getInventoryReport: (params) => api.get("/reports/inventory", { params }),
  getProfitLossReport: (params) => api.get("/reports/profit-loss", { params }),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get("/analytics/dashboard"),
  getSalesTrends: (params) => api.get("/analytics/sales-trends", { params }),
};


export const pharmacyPatientsAPI = {
  getPharmacyPatients: (params) => api.get("/sales/pharmacy/getPharmacyPatients", { params }),
};


export const prescriptionsAPI = {
  getDashboard: () => api.get("/analytics/dashboard"),
  getSalesTrends: (params) => api.get("/analytics/sales-trends", { params }),
};

export default api;
